import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalApiService from '../../services/PortalApiService';
import PortalAuthService from '../../services/PortalAuthService';
import Loader from '../../components/Loader';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import StatusBadge from '../../components/ui/StatusBadge';
import TrendChart from '../../components/ui/TrendChart';
import Avatar from '../../components/ui/Avatar';
import { formatDate } from '../../utils/helper';

const currency = process.env.REACT_APP_CURRENCY || 'AED';
const money = (v) => `${currency} ${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const PortalDashboard = () => {
    const [company, setCompany] = useState(PortalAuthService.getCurrentUser());
    const [data, setData] = useState(null);

    useEffect(() => {
        // Refresh the stored profile (admin may have changed areas / details)
        PortalApiService.request({ method: 'GET', url: '/getProfile' })
            .then(({ data: res }) => {
                if (res.status) {
                    const updated = { ...res.data.company, auth_token: company.auth_token };
                    PortalAuthService.setCurrentUser(updated);
                    setCompany(updated);
                }
            })
            .catch(() => {});

        PortalApiService.request({ method: 'GET', url: '/portal-dashboard' })
            .then(({ data: res }) => res.status && setData(res.data))
            .catch(() => setData({}));
    }, []);

    const isFacility = company?.type === 'facility';

    if (!data) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Loader text="Loading dashboard..." size="40" />
            </div>
        );
    }

    const bookings = data.bookings || {};
    const services = data.services || {};
    const months = data.monthly_bookings || [];
    const needsAction = (bookings.Requested || 0);
    const active = (bookings.Confirmed || 0) + (bookings['In-Progress'] || 0);

    return (
        <div className="hp-page">
            <PageHeader
                title={`Welcome back, ${company?.name || ''}`}
                subtitle={isFacility ? 'Manage your facility services and bookings in one place.' : 'Manage your services and bookings in one place.'}
                actions={(
                    <>
                        <Link to="/services/create" className="btn btn-sm hp-btn-outline"><i className="bi bi-plus-lg me-1"></i> Add service</Link>
                        <Link to="/bookings" className="btn btn-sm btn-primary"><i className="bi bi-calendar2-check me-1"></i> Bookings</Link>
                    </>
                )}
            />

            {needsAction > 0 && (
                <Link to="/bookings?status=Requested" className="hp-card d-flex align-items-center gap-3 p-4 mb-4 text-decoration-none" style={{ borderColor: '#f3d48a' }}>
                    <span className="hp-stat-icon hp-tone-warning"><i className="bi bi-bell"></i></span>
                    <div className="flex-grow-1">
                        <div className="fw-bold text-dark">{needsAction} new booking request{needsAction > 1 ? 's' : ''} waiting for you</div>
                        <div className="text-muted fs-7">Confirm or cancel them so customers know what's next.</div>
                    </div>
                    <i className="bi bi-arrow-right text-dark fs-4"></i>
                </Link>
            )}

            <div className="row g-4 mb-4">
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Active bookings" value={active} icon="bi-calendar2-check" tone="brand" to="/bookings?status=Active"
                        foot={`${bookings['In-Progress'] || 0} in progress`} />
                </div>
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Completed" value={bookings.Completed || 0} icon="bi-check2-circle" tone="info" to="/bookings?status=Completed"
                        foot={`${bookings.all || 0} bookings in total`} />
                </div>
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Earnings" value={money(data.revenue?.total)} icon="bi-cash-stack" tone="violet"
                        foot={`${money(data.revenue?.this_month)} this month`} />
                </div>
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Services" value={services.all || 0} icon="bi-tools" tone="warning" to="/services"
                        foot={`${services.active || 0} active · ${services.disabled || 0} disabled`} />
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-xl-8">
                    <div className="hp-card h-100">
                        <div className="hp-card-head pb-3">
                            <div>
                                <h3 className="hp-card-title">Latest bookings</h3>
                                <p className="hp-card-sub">Your most recent customer bookings</p>
                            </div>
                            <Link to="/bookings" className="btn btn-sm hp-btn-outline">View all</Link>
                        </div>
                        <div className="table-responsive">
                            <table className="table hp-table table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Booking</th>
                                        <th>Customer</th>
                                        <th>Scheduled</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.recent_bookings || []).length > 0 ? data.recent_bookings.map(b => (
                                        <tr key={b._id}>
                                            <td>
                                                <Link to={`/bookings/${b._id}`} className="fw-bold text-dark text-hover-primary">#{b.job_code}</Link>
                                                <div className="text-muted fs-7">{b.service_id?.name || '—'}</div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Avatar src={b.user_id?.picture} name={b.user_id?.name} size={30} />
                                                    <span className="text-dark">{b.user_id?.name || '—'}</span>
                                                </div>
                                            </td>
                                            <td>{b.date ? formatDate(b.date, 'DD MMM YYYY') : '—'} {b.time && <span className="text-muted fs-7">· {b.time}</span>}</td>
                                            <td><StatusBadge status={b.status} /></td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4">
                                                <div className="hp-empty">
                                                    <i className="bi bi-calendar2-x"></i>
                                                    No bookings yet. They'll appear here as soon as customers book your services.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="hp-card h-100">
                        <div className="hp-card-head">
                            <div>
                                <h3 className="hp-card-title">Bookings trend</h3>
                                <p className="hp-card-sub">Last 6 months</p>
                            </div>
                        </div>
                        <div className="hp-card-body">
                            <TrendChart type="bar" labels={months.map(m => m.label)} data={months.map(m => m.count)} label="Bookings" height={240} />
                        </div>
                    </div>
                </div>
            </div>

            {isFacility && (
                <div className="hp-card">
                    <div className="hp-card-head">
                        <div>
                            <h3 className="hp-card-title">Service areas</h3>
                            <p className="hp-card-sub">Residents with approved properties in these areas can see and book your services.</p>
                        </div>
                    </div>
                    <div className="hp-card-body d-flex flex-wrap gap-2">
                        {(company.areas || []).length > 0
                            ? company.areas.map(a => (
                                <span key={a} className="hp-chip"><i className="bi bi-geo-alt"></i>{a}</span>
                            ))
                            : <span className="text-muted">No areas assigned yet. Contact the Helpio admin.</span>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortalDashboard;
